import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import withRouter from "components/Common/withRouter";
import {
  Card, CardBody, CardHeader, Col, Container, Row,
  Table, Button, Spinner, Badge,
  Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input
} from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import { syncInvoiceToParasut } from "../../helpers/parasut_helper";

const fmt = (n) => (parseFloat(n) || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 }) + " ₺";
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" }) : "—";
const fmtDateTime = (d) => d ? new Date(d).toLocaleString("tr-TR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const statusColor = (s) => ({ Ödendi: "success", Bekliyor: "warning", İptal: "danger" }[s] ?? "secondary");

const InvoiceDetail = (props) => {
  document.title = "Fatura Detayı | LojGo Yönetim";
  const id = props.router?.params?.id ?? null;

  const [invoice, setInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSavingItem, setIsSavingItem] = useState(false);
  const [newItem, setNewItem] = useState({ ProductName: "", Quantity: 1, UnitPrice: "", VatRate: 20, Unit: "Adet", Discount_Rate: 0 });

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    if (isModalOpen) setNewItem({ ProductName: "", Quantity: 1, UnitPrice: "", VatRate: 20, Unit: "Adet", Discount_Rate: 0 });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveNewItem = async () => {
    if (!newItem.ProductName || !newItem.UnitPrice) {
      toast.warning("Lütfen hizmet adı ve birim fiyatı doldurunuz.");
      return;
    }

    setIsSavingItem(true);
    try {
      const newQ = parseFloat(newItem.Quantity) || 1;
      const newP = parseFloat(newItem.UnitPrice) || 0;
      const newVat = newItem.VatRate !== "" ? parseFloat(newItem.VatRate) : 20;
      const newDisc = parseFloat(newItem.Discount_Rate) || 0;

      const lineResponse = await fetch("/api/hiflow/Invoince_Lines", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Invoince_ID: invoice.ID,
          ProductName: newItem.ProductName,
          Quantity: newQ,
          UnitPrice: newP,
          VatRate: newVat,
          Unit: newItem.Unit || "Adet",
          Discount_Rate: newDisc
        }),
      });

      if (!lineResponse.ok) throw new Error("Kalem veritabanına kaydedilemedi.");

      const existingLines = invoice.Invoince_Lines || [];
      let totalHT = 0;   
      let totalVAT = 0;  

      existingLines.forEach(line => {
        const q = parseFloat(line.Quantity) || 1;
        const p = parseFloat(line.UnitPrice) || 0;
        const vRate = line.VatRate !== null && line.VatRate !== undefined ? parseFloat(line.VatRate) : 20;
        const dRate = parseFloat(line.Discount_Rate) || 0;
        
        const lineSubtotal = (q * p) * (1 - dRate / 100);
        totalHT += lineSubtotal;
        totalVAT += lineSubtotal * (vRate / 100);
      });

      const newLineSubtotal = (newQ * newP) * (1 - newDisc / 100);
      totalHT += newLineSubtotal;
      totalVAT += newLineSubtotal * (newVat / 100);

      const amountFees = parseFloat(invoice.Amount_Fees) || 0;
      const finalTotalAmount = totalHT + totalVAT + amountFees;

      const invoiceResponse = await fetch(`/api/hiflow/Invoince(${invoice.ID})`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ Amount_HT: totalHT, Amount_VAT: totalVAT, TotalAmount: finalTotalAmount })
      });

      if (!invoiceResponse.ok) throw new Error("Fatura genel toplamı güncellenemedi.");

      toast.success("Kalem eklendi ve fatura toplamı eşitlendi!");
      toggleModal();
      fetchInvoiceData();

    } catch (error) {
      console.error(error);
      toast.error(`Hata: ${error.message}`);
    } finally {
      setIsSavingItem(false);
    }
  };

  const fetchInvoiceData = useCallback(async () => {
    try {
      const res = await fetch(`https://hiflowapi.stechomeyazilim.info/Invoince?$filter=ID eq ${id}&$expand=Users($expand=Users_BankAccount),Users_Invoince,Invoince_Lines`);
      const data = await res.json();
      const invoiceData = data?.value ? data.value[0] : data;
      if (invoiceData) setInvoice(invoiceData);
      else toast.error("Fatura bulunamadı.");
    } catch {
      toast.error("Fatura detayları yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) { setIsLoading(true); fetchInvoiceData(); } 
    else setIsLoading(false);
  }, [id, fetchInvoiceData]);

  const handleSyncToParasut = async () => {
    if (!invoice) return;
    setIsSending(true);
    try {
      const parasutId = await syncInvoiceToParasut(invoice);
      setInvoice((prev) => ({ ...prev, Parasut_Invoice_ID: parasutId }));
      toast.success(`Fatura Paraşüt'e başarıyla gönderildi! ID: ${parasutId}`);
    } catch (error) {
      toast.error(`Paraşüt hatası: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="page-content d-flex justify-content-center align-items-center" style={{ minHeight: "70vh" }}>
        <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="page-content text-center py-5">
        <i className="bx bx-error-circle text-danger font-size-48 d-block mb-3"></i>
        <h4>Fatura bilgisi alınamadı.</h4>
        <Link to="/invoices" className="btn btn-primary mt-2">Fatura Listesine Dön</Link>
      </div>
    );
  }

  const amountHT    = parseFloat(invoice.Amount_HT)    || 0;
  const amountVAT   = parseFloat(invoice.Amount_VAT)   || 0;
  const amountFees  = parseFloat(invoice.Amount_Fees)  || 0;
  const totalAmount = parseFloat(invoice.TotalAmount)  || 0;

  const vatRate = amountHT > 0 ? Math.round((amountVAT / amountHT) * 100) : 20;

  const bankAccount    = invoice.Users?.Users_BankAccount?.[0] ?? null;
  const invoiceInfo    = invoice.Users_Invoince ?? {};
  const customer       = invoice.Users ?? {};

  const isEInvoice     = !!invoiceInfo.E_Invoice_ID;
  const isParasutSynced = !!invoice.Parasut_Invoice_ID;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="d-print-none">
            <Breadcrumbs title="Finans" breadcrumbItem="Fatura Detayı" />
          </div>

          {/* ... (Diğer tüm UI kartları aynı kalıyor - Yukarıdaki UI ile tamamen aynı) ... */}
           {/* ── Durum Bandı ─────────────────────────────────────── */}
           {invoice.Invoince_Status && (
            <div
              className={`d-print-none alert alert-${statusColor(invoice.Invoince_Status)} d-flex align-items-center justify-content-between mb-3 py-2 px-3`}
              role="alert"
            >
              <span>
                <i className={`bx ${invoice.Invoince_Status === "Ödendi" ? "bx-check-circle" : invoice.Invoince_Status === "İptal" ? "bx-x-circle" : "bx-time-five"} me-2`}></i>
                <strong>Ödeme Durumu:</strong>&nbsp;{invoice.Invoince_Status}
              </span>
              <Badge color={statusColor(invoice.Invoince_Status)} className="font-size-13 px-3 py-2">
                {invoice.Invoince_Status}
              </Badge>
            </div>
          )}

          {/* ── Özet Bilgi Kartları ──────────────────────────────── */}
          <Row className="mb-4 d-print-none">
            {[
              {
                label: "Fatura Numarası",
                value: invoice.Invoince_Number || "—",
                icon: "bx-receipt",
                color: "primary",
              },
              {
                label: "Fatura Tipi",
                value: isEInvoice ? "E-Fatura" : "E-Arşiv Fatura",
                icon: "bx-file",
                color: "info",
              },
              {
                label: "Toplam Tutar",
                value: fmt(totalAmount),
                icon: "bx-money",
                color: "success",
              },
              {
                label: "Oluşturma Tarihi",
                value: fmtDate(invoice.Created_DateTime),
                icon: "bx-calendar",
                color: "warning",
              },
            ].map((c, i) => (
              <Col sm={6} xl={3} key={i} className="mb-3">
                <Card className="border-0 shadow-sm h-100 mb-0">
                  <CardBody className="d-flex align-items-center gap-3 py-3">
                    <div
                      className={`rounded d-flex align-items-center justify-content-center bg-${c.color} bg-opacity-10`}
                      style={{ width: 46, height: 46, flexShrink: 0 }}
                    >
                      <i className={`bx ${c.icon} font-size-22 text-${c.color}`}></i>
                    </div>
                    <div className="overflow-hidden">
                      <p className="text-muted mb-0 font-size-12">{c.label}</p>
                      <h5 className="mb-0 fw-bold text-dark text-truncate">{c.value}</h5>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          {/* ══ FATURA KARTI (Yazdırılabilir Alan) ═════════════════ */}
          <Card id="invoice-card" className="border-0 shadow-sm">
            <CardBody className="p-4 p-md-5">

              {/* ── Başlık: Şirket + Fatura Bilgisi ─────────────── */}
              <div className="d-flex justify-content-between align-items-start border-bottom pb-4 mb-4 flex-wrap gap-3">
                {/* Sol: Şirket */}
                <div>
                  <h2 className="fw-bold text-primary mb-1" style={{ letterSpacing: "0.05em" }}>LOJGO</h2>
                  <p className="text-muted mb-1 fw-semibold">Araç Taşıma Hizmetleri</p>
                  <p className="text-muted mb-0 font-size-13">
                    Maslak Mah. Büyükdere Cad. No:123 Şişli/İstanbul
                  </p>
                  <p className="text-muted mb-0 font-size-13">
                    Tel: +90 212 000 00 00 &nbsp;|&nbsp; E-posta: fatura@lojgo.com
                  </p>
                </div>

                {/* Sağ: Fatura Kimliği */}
                <div className="text-end">
                  <div className="d-flex align-items-center justify-content-end gap-2 mb-2">
                    <Badge color={isEInvoice ? "primary" : "info"} className="font-size-12 px-3 py-2">
                      {isEInvoice ? "E-FATURA" : "E-ARŞİV FATURA"}
                    </Badge>
                    <Badge color={statusColor(invoice.Invoince_Status)} className="font-size-12 px-3 py-2">
                      {invoice.Invoince_Status || "Bekliyor"}
                    </Badge>
                  </div>
                  <h5 className="mb-1">
                    <span className="text-muted fw-normal">Fatura No: </span>
                    <span className="text-dark fw-bold">{invoice.Invoince_Number || "Taslak"}</span>
                  </h5>
                  {isEInvoice && invoiceInfo.E_Invoice_ID && (
                    <p className="text-muted mb-1 font-size-12">
                      E-Fatura UUID: <span className="font-monospace">{invoiceInfo.E_Invoice_ID}</span>
                    </p>
                  )}
                  <p className="text-muted mb-1 font-size-13">
                    <strong>Düzenleme Tarihi:</strong> {fmtDate(invoice.Created_DateTime)}
                  </p>
                  <p className="text-muted mb-0 font-size-13">
                    <strong>Oluşturulma:</strong> {fmtDateTime(invoice.Created_DateTime)}
                  </p>
                </div>
              </div>

              {/* ── Alıcı + Banka Bilgileri ──────────────────────── */}
              <Row className="mb-4 g-4">
                {/* Alıcı */}
                <Col md={6}>
                  <div className="border rounded p-3 h-100">
                    <h6 className="text-uppercase text-muted fw-bold mb-3 font-size-11 d-flex align-items-center gap-1">
                      <i className="bx bx-user-circle font-size-16"></i>
                      Fatura Edilen (Alıcı)
                    </h6>
                    <h5 className="fw-bold text-dark mb-2">
                      {customer.Users_NameSurname || "Müşteri Bilgisi Yok"}
                    </h5>
                    <table className="w-100 font-size-13 text-muted">
                      <tbody>
                        {invoiceInfo.Adres && (
                          <tr>
                            <td className="pe-2 pb-1 text-nowrap fw-semibold text-dark-50" style={{ width: "1%" }}>
                              <i className="bx bx-map-pin me-1"></i>Adres:
                            </td>
                            <td className="pb-1">{invoiceInfo.Adres}</td>
                          </tr>
                        )}
                        {invoiceInfo.VergiDairesi && (
                          <tr>
                            <td className="pe-2 pb-1 text-nowrap fw-semibold">
                              <i className="bx bx-building me-1"></i>Vergi Dairesi:
                            </td>
                            <td className="pb-1">{invoiceInfo.VergiDairesi}</td>
                          </tr>
                        )}
                        <tr>
                          <td className="pe-2 pb-1 text-nowrap fw-semibold">
                            <i className="bx bx-id-card me-1"></i>VKN / TCKN:
                          </td>
                          <td className="pb-1 font-monospace">
                            {invoiceInfo.KdvNumber || customer.Users_Tc || "—"}
                          </td>
                        </tr>
                        {customer.Users_MailAddress && (
                          <tr>
                            <td className="pe-2 pb-1 text-nowrap fw-semibold">
                              <i className="bx bx-envelope me-1"></i>E-posta:
                            </td>
                            <td className="pb-1">{customer.Users_MailAddress}</td>
                          </tr>
                        )}
                        {invoiceInfo.Parasut_Contact_ID && (
                          <tr>
                            <td className="pe-2 pb-1 text-nowrap fw-semibold">
                              <i className="bx bx-cloud me-1"></i>Paraşüt Cari:
                            </td>
                            <td className="pb-1 font-monospace text-primary">
                              #{invoiceInfo.Parasut_Contact_ID}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Col>

                {/* Banka Bilgisi */}
                <Col md={6}>
                  <div className="border rounded p-3 h-100">
                    <h6 className="text-uppercase text-muted fw-bold mb-3 font-size-11 d-flex align-items-center gap-1">
                      <i className="bx bx-credit-card font-size-16"></i>
                      Ödeme Yapılacak Hesap
                    </h6>
                    {bankAccount ? (
                      <table className="w-100 font-size-13 text-muted">
                        <tbody>
                          <tr>
                            <td className="pe-2 pb-2 text-nowrap fw-semibold text-dark" style={{ width: "1%" }}>
                              <i className="bx bx-buildings me-1"></i>Banka:
                            </td>
                            <td className="pb-2 fw-bold text-dark">{bankAccount.Users_BankAccount_BankName || "—"}</td>
                          </tr>
                          <tr>
                            <td className="pe-2 pb-2 text-nowrap fw-semibold">
                              <i className="bx bx-user me-1"></i>Hesap Sahibi:
                            </td>
                            <td className="pb-2">{bankAccount.Users_BankAccount_HolderName || "—"}</td>
                          </tr>
                          <tr>
                            <td className="pe-2 pb-2 text-nowrap fw-semibold">
                              <i className="bx bx-transfer me-1"></i>IBAN:
                            </td>
                            <td className="pb-2">
                              <span className="font-monospace fw-bold text-primary font-size-13">
                                {bankAccount.Users_BankAccount_Iban || "—"}
                              </span>
                            </td>
                          </tr>
                          {bankAccount.Users_BankAccount_Bic && (
                            <tr>
                              <td className="pe-2 pb-1 text-nowrap fw-semibold">
                                <i className="bx bx-code-block me-1"></i>BIC/SWIFT:
                              </td>
                              <td className="pb-1 font-monospace">{bankAccount.Users_BankAccount_Bic}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    ) : (
                      <div className="text-center py-3 text-muted">
                        <i className="bx bx-error-circle font-size-32 text-warning d-block mb-2"></i>
                        <span className="font-size-13">
                          Sistemde kayıtlı banka hesabı bulunamadı.
                        </span>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>

              {/* ── Hizmet Kalemleri Tablosu ─────────────────────── */}
              <div className="d-flex justify-content-between align-items-center mt-5 mb-2 d-print-none">
                <h6 className="text-uppercase text-muted fw-bold font-size-12 mb-0">
                  <i className="bx bx-list-ul me-1"></i> Fatura Kalemleri
                </h6>
                <Button color="primary" size="sm" onClick={toggleModal} disabled={isParasutSynced}>
                  <i className="bx bx-plus me-1"></i>Yeni Kalem Ekle
                </Button>
              </div>
              {isParasutSynced && (
                <small className="text-danger d-block mb-2 d-print-none">
                  * Fatura Paraşüt'e aktarıldığı için yeni kalem eklenemez.
                </small>
              )}
            <div className="table-responsive mt-2">
                <Table className="table-bordered align-middle mb-0" style={{ fontSize: "0.9rem" }}>
                  <thead style={{ background: "#f8f9fa" }}>
                    <tr>
                      <th style={{ width: "50px" }} className="text-center">#</th>
                      <th>Hizmet / Ürün Açıklaması</th>
                      <th className="text-center" style={{ width: "80px" }}>Birim</th>
                      <th className="text-center" style={{ width: "80px" }}>Miktar</th>
                      <th className="text-end" style={{ width: "120px" }}>Birim Fiyat</th>
                      <th className="text-center" style={{ width: "90px" }}>İndirim</th>
                      <th className="text-center" style={{ width: "80px" }}>KDV %</th>
                      <th className="text-end" style={{ width: "120px" }}>KDV Tutarı</th>
                      <th className="text-end" style={{ width: "130px" }}>Toplam (KDV'siz)</th>
                    </tr>
                  </thead>
                <tbody>
                    {invoice.Invoince_Lines && invoice.Invoince_Lines.length > 0 ? (
                      invoice.Invoince_Lines.map((line, index) => {
                        const q = parseFloat(line.Quantity) || 1;
                        const price = parseFloat(line.UnitPrice) || 0;
                        const vRate = line.VatRate !== null && line.VatRate !== undefined ? parseFloat(line.VatRate) : 20;
                        const dRate = parseFloat(line.Discount_Rate) || 0;
                        
                        const rowTotalHT = (q * price) * (1 - dRate / 100);
                        const rowVAT = rowTotalHT * (vRate / 100);

                        return (
                          <tr key={line.ID}>
                            <td className="text-center text-muted">{index + 1}</td>
                            <td>
                              <p className="mb-0 fw-semibold text-dark">{line.ProductName || "Hizmet / Ürün"}</p>
                              <small className="text-muted">
                                Ref. No: #{invoice.ID}
                                {invoice.Invoince_Number ? ` — Fatura: ${invoice.Invoince_Number}` : ""}
                              </small>
                            </td>
                            <td className="text-center text-muted">{line.Unit || "Adet"}</td>
                            <td className="text-center">{q}</td>
                            <td className="text-end">{fmt(price)}</td>
                            <td className="text-center">
                               {dRate > 0 ? <Badge color="soft-danger text-danger">%{dRate}</Badge> : "—"}
                            </td>
                            <td className="text-center">%{vRate}</td>
                            <td className="text-end text-muted">{fmt(rowVAT)}</td>
                            <td className="text-end fw-semibold">{fmt(rowTotalHT)}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td className="text-center text-muted">1</td>
                        <td>
                          <p className="mb-0 fw-semibold text-dark">Araç Taşıma Operasyon Hizmeti</p>
                          <small className="text-muted">
                            Ref. No: #{invoice.ID}
                            {invoice.Invoince_Number ? ` — Fatura: ${invoice.Invoince_Number}` : ""}
                          </small>
                        </td>
                        <td className="text-center text-muted">Adet</td>
                        <td className="text-center">1</td>
                        <td className="text-end">{fmt(amountHT)}</td>
                        <td className="text-center">—</td>
                        <td className="text-center">%{vatRate}</td>
                        <td className="text-end text-muted">{fmt(amountVAT)}</td>
                        <td className="text-end fw-semibold">{fmt(amountHT)}</td>
                      </tr>
                    )}

                    {amountFees > 0 && (
                      <tr>
                        <td className="text-center text-muted">
                          {(invoice.Invoince_Lines?.length || 1) + 1}
                        </td>
                        <td>
                          <p className="mb-0 fw-semibold text-dark">Ek Hizmet / Masraflar</p>
                          <small className="text-muted">Ekstra ücretler ve operasyon giderleri</small>
                        </td>
                        <td className="text-center text-muted">Adet</td>
                        <td className="text-center">1</td>
                        <td className="text-end">{fmt(amountFees)}</td>
                        <td className="text-center">—</td>
                        <td className="text-center">%0</td>
                        <td className="text-end text-muted">—</td>
                        <td className="text-end fw-semibold">{fmt(amountFees)}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* ── Tutar Özeti ──────────────────────────────────── */}
              <Row className="justify-content-end mt-4">
                <Col md={5} lg={4}>
                  <div className="border rounded overflow-hidden">
                    <table className="table table-sm mb-0">
                      <tbody>
                        <tr>
                          <td className="text-muted px-3">Ara Toplam (Matrah)</td>
                          <td className="text-end px-3 fw-semibold">{fmt(amountHT)}</td>
                        </tr>
                        {amountFees > 0 && (
                          <tr>
                            <td className="text-muted px-3">Ek Masraflar</td>
                            <td className="text-end px-3 fw-semibold">{fmt(amountFees)}</td>
                          </tr>
                        )}
                        <tr>
                          <td className="text-muted px-3">KDV</td>
                          <td className="text-end px-3 fw-semibold">{fmt(amountVAT)}</td>
                        </tr>
                        <tr className="table-primary">
                          <td className="px-3 fw-bold text-dark">
                            <strong>GENEL TOPLAM</strong>
                          </td>
                          <td className="text-end px-3">
                            <strong className="text-primary font-size-16">{fmt(totalAmount)}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Col>
              </Row>

              {/* ── Notlar / Açıklama ────────────────────────────── */}
              <div className="mt-4 p-3 bg-light rounded">
                <h6 className="text-muted text-uppercase fw-bold font-size-11 mb-2">
                  <i className="bx bx-note me-1"></i>Notlar &amp; Açıklama
                </h6>
                <p className="mb-0 font-size-13 text-muted">
                  Bu fatura LojGo Araç Taşıma Hizmetleri kapsamında düzenlenmiştir.
                  Ödeme bilgileri için yukarıdaki banka hesap bilgilerini kullanınız.
                  Sorularınız için fatura@lojgo.com adresine ulaşabilirsiniz.
                </p>
              </div>

            </CardBody>
          </Card>

          {/* ══ Paraşüt Durum Kartı ════════════════════════════════ */}
          <Card className="d-print-none border-0 shadow-sm mt-4">
            <CardHeader className="bg-white border-bottom d-flex align-items-center gap-2 py-3">
              <i className="bx bx-cloud font-size-20 text-primary"></i>
              <h5 className="mb-0 fw-bold">Paraşüt Muhasebe Entegrasyonu</h5>
              {isParasutSynced ? (
                <Badge color="success" className="ms-auto font-size-12 px-3 py-2">
                  <i className="bx bx-check me-1"></i>Senkronize
                </Badge>
              ) : (
                <Badge color="warning" className="ms-auto font-size-12 px-3 py-2">
                  <i className="bx bx-time me-1"></i>Bekliyor
                </Badge>
              )}
            </CardHeader>
            <CardBody>
              <Row className="align-items-center">
                <Col md={7}>
                  {isParasutSynced ? (
                    <div>
                      <div className="d-flex align-items-center gap-3 mb-3">
                        <div
                          className="rounded-circle bg-success bg-opacity-10 d-flex align-items-center justify-content-center"
                          style={{ width: 52, height: 52 }}
                        >
                          <i className="bx bx-cloud-check font-size-24 text-success"></i>
                        </div>
                        <div>
                          <h5 className="mb-0 fw-bold text-success">Paraşüt'e Aktarıldı</h5>
                          <p className="mb-0 text-muted font-size-13">
                            Fatura Paraşüt muhasebe sisteminde kayıtlıdır.
                          </p>
                        </div>
                      </div>
                      <table className="table table-sm table-borderless mb-0" style={{ maxWidth: 420 }}>
                        <tbody>
                          <tr>
                            <td className="text-muted font-size-13 ps-0" style={{ width: "35%" }}>Paraşüt Fatura ID:</td>
                            <td className="fw-bold font-monospace text-primary">#{invoice.Parasut_Invoice_ID}</td>
                          </tr>
                          {invoiceInfo.Parasut_Contact_ID && (
                            <tr>
                              <td className="text-muted font-size-13 ps-0">Paraşüt Cari ID:</td>
                              <td className="fw-bold font-monospace">#{invoiceInfo.Parasut_Contact_ID}</td>
                            </tr>
                          )}
                          <tr>
                            <td className="text-muted font-size-13 ps-0">Fatura Tutarı:</td>
                            <td className="fw-bold">{fmt(totalAmount)}</td>
                          </tr>
                          <tr>
                            <td className="text-muted font-size-13 ps-0">Müşteri:</td>
                            <td className="fw-bold">{customer.Users_NameSurname || "—"}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="rounded-circle bg-warning bg-opacity-10 d-flex align-items-center justify-content-center"
                        style={{ width: 52, height: 52 }}
                      >
                        <i className="bx bx-cloud-upload font-size-24 text-warning"></i>
                      </div>
                      <div>
                        <h5 className="mb-1 fw-bold text-dark">Henüz Gönderilmedi</h5>
                        <p className="mb-0 text-muted font-size-13">
                          Bu fatura Paraşüt muhasebe sistemine aktarılmamıştır.
                          Aktarmak için "Paraşüt'e Gönder" butonunu kullanın.
                        </p>
                        {!invoiceInfo.KdvNumber && !customer.Users_Tc && (
                          <div className="alert alert-warning py-1 px-2 mt-2 mb-0 font-size-12">
                            <i className="bx bx-error-circle me-1"></i>
                            VKN / TCKN eksik — gönderim sonrası Paraşüt'te anonim cari oluşabilir.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </Col>

                <Col md={5} className="text-md-end mt-3 mt-md-0">
                  {isParasutSynced ? (
                    <span className="btn btn-success disabled opacity-75">
                      <i className="bx bx-cloud-check me-2"></i>
                      Paraşüt'te Kayıtlı
                    </span>
                  ) : (
                    <Button
                      color="primary"
                      size="lg"
                      disabled={isSending}
                      onClick={handleSyncToParasut}
                      className="px-4"
                    >
                      {isSending ? (
                        <><Spinner size="sm" className="me-2" />Gönderiliyor...</>
                      ) : (
                        <><i className="bx bx-cloud-upload me-2"></i>Paraşüt'e Gönder</>
                      )}
                    </Button>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* ══ Eylem Butonları ════════════════════════════════════ */}
          <div className="d-print-none d-flex justify-content-between align-items-center mt-4 mb-5 flex-wrap gap-2">
            <Link to="/invoices" className="btn btn-outline-secondary">
              <i className="bx bx-arrow-back me-1"></i>Listeye Dön
            </Link>
            <div className="d-flex gap-2 flex-wrap">
              <Button
                color="light"
                onClick={() => window.print()}
                className="border"
              >
                <i className="bx bx-printer me-1"></i>Yazdır / PDF
              </Button>
            </div>
          </div>

        </Container>

        {/* ══ YENİ KALEM EKLE MODAL ════════════════════════════════ */}
        <Modal isOpen={isModalOpen} toggle={toggleModal} centered backdrop="static">
          <ModalHeader toggle={toggleModal}>Yeni Hizmet / Ürün Ekle</ModalHeader>
          <ModalBody>
            <Form>
              <FormGroup className="mb-3">
                <Label>Hizmet / Ürün Açıklaması <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  name="ProductName"
                  placeholder="Örn: Ankara-İstanbul Araç Taşıma"
                  value={newItem.ProductName}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <Row>
                <Col md={4}>
                  <FormGroup className="mb-3">
                    <Label>Miktar</Label>
                    <Input
                      type="number"
                      name="Quantity"
                      min="1"
                      step="0.01"
                      value={newItem.Quantity}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup className="mb-3">
                    <Label>Birim</Label>
                    <Input
                      type="select"
                      name="Unit"
                      value={newItem.Unit}
                      onChange={handleInputChange}
                    >
                      <option value="Adet">Adet</option>
                      <option value="Saat">Saat</option>
                      <option value="Gün">Gün</option>
                      <option value="Ay">Ay</option>
                      <option value="Km">Km</option>
                      <option value="kg">kg</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup className="mb-3">
                    <Label>KDV Oranı (%)</Label>
                    <Input
                      type="select"
                      name="VatRate"
                      value={newItem.VatRate}
                      onChange={handleInputChange}
                    >
                      <option value={0}>%0</option>
                      <option value={1}>%1</option>
                      <option value={8}>%8</option>
                      <option value={10}>%10</option>
                      <option value={18}>%18</option>
                      <option value={20}>%20</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <FormGroup className="mb-3">
                    <Label>Birim Fiyat (₺) <span className="text-danger">*</span></Label>
                    <Input
                      type="number"
                      name="UnitPrice"
                      placeholder="0.00"
                      step="0.01"
                      value={newItem.UnitPrice}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup className="mb-3">
                    <Label>İndirim Oranı (%)</Label>
                    <Input
                      type="number"
                      name="Discount_Rate"
                      placeholder="0"
                      min="0"
                      max="100"
                      step="0.01"
                      value={newItem.Discount_Rate}
                      onChange={handleInputChange}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleModal} disabled={isSavingItem}>
              İptal
            </Button>
            <Button color="primary" onClick={handleSaveNewItem} disabled={isSavingItem}>
              {isSavingItem ? <Spinner size="sm" /> : "Kalemi Kaydet"}
            </Button>
          </ModalFooter>
        </Modal>
        
      </div>

      {/* ── Baskı Stilleri ─────────────────────────────────────── */}
      <style>{`
        @media print {
          .page-content { padding: 0 !important; margin: 0 !important; }
          .d-print-none { display: none !important; }
          #invoice-card { box-shadow: none !important; border: none !important; }
          body { background: #fff !important; }
          .card { border: none !important; box-shadow: none !important; }
          .table-bordered td, .table-bordered th { border: 1px solid #dee2e6 !important; }
        }
      `}</style>

      <ToastContainer autoClose={2500} position="bottom-right" />
    </React.Fragment>
  );
};

export default withRouter(InvoiceDetail);